import { useEffect, useState, useMemo, useCallback } from "react";
import { AlertCircle, Download } from "lucide-react";
import {
  fetchReimbursementAudit,
  type ReimbursementPoolAudit,
} from "../../api/client";
import type { Tenant } from "../../api/types";
import { fmtCurrency, fmtPercent, fmtSF, fmtCamType } from "../../utils/format";
import { downloadCSV } from "../../utils/csv";
import LoadingSpinner from "../LoadingSpinner";

// ---------- types ----------

interface ReimbursementTabProps {
  dealId: string;
  scenarioId: string;
  tenants: Tenant[];
  holdYears: number;
}

// ---------- helpers ----------

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between py-1 border-b border-grove-100 last:border-0">
      <span className="text-[11px] text-bark-500">{label}</span>
      <span className={`text-[11px] text-bark-700 font-medium ${mono ? "tabular-nums" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <h5 className="text-[11px] font-semibold text-forest-700 uppercase tracking-wider mb-1.5">
        {title}
      </h5>
      {children}
    </div>
  );
}

// ---------- Breakdown card ----------

function BreakdownCard({ rec }: { rec: ReimbursementPoolAudit }) {
  // Engine emits lowercase enum values (base_year_stop, expense_cap) —
  // normalize before comparing.
  const camType = (rec.cam_type || "").toLowerCase();
  const isBaseYear = camType === "base_year_stop" || camType === "expense_cap";
  const isCap = camType === "expense_cap";

  return (
    <div className="bg-white border border-grove-300 rounded-xl p-5 shadow-sm">
      <h4 className="text-sm font-semibold text-bark-700 mb-4">
        {rec.tenant_name}
        <span className="ml-2 text-bark-400 font-normal">Year {rec.year} ({rec.calendar_year})</span>
      </h4>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
        {/* Left column */}
        <div>
          <Section title="CAM Structure">
            <Row label="Type" value={fmtCamType(rec.cam_type)} />
          </Section>

          <Section title="Pro-Rata Share">
            <Row label="Leased SF" value={fmtSF(rec.leased_sf)} mono />
            <Row label="Total Leasable Area" value={fmtSF(rec.total_leasable_area)} mono />
            <Row label="Computed Pro-Rata" value={fmtPercent(rec.computed_pro_rata, 1)} mono />
          </Section>

          <Section title="CAM Pool Construction">
            <Row label="Pool Type" value={rec.cam_pool_type} />
            <Row label="Variable Expenses" value={fmtCurrency(rec.variable_expenses)} mono />
            <Row label="Fixed Expenses" value={fmtCurrency(rec.fixed_expenses)} mono />
            <Row label="Occupancy Rate" value={fmtPercent(rec.occupancy_rate, 1)} mono />
            <Row label="Gross-Up Threshold" value={fmtPercent(rec.gross_up_threshold, 1)} mono />
            <Row label="Grossed-Up Variable" value={fmtCurrency(rec.grossed_up_variable)} mono />
            <Row
              label="Exclusions Applied"
              value={rec.exclusions_applied.length > 0 ? rec.exclusions_applied.join(", ") : "None"}
            />
            <Row label="Mgmt Fee Load %" value={fmtPercent(rec.management_fee_load_pct, 1)} mono />
            <Row label="CAM Pool (Before Load)" value={fmtCurrency(rec.cam_pool_before_load)} mono />
            <Row label="CAM Pool (After Load)" value={fmtCurrency(rec.cam_pool_after_load)} mono />
          </Section>

          {isBaseYear && (
            <Section title="Base Year">
              <Row label="Base Year" value={rec.base_year != null ? String(rec.base_year) : "--"} />
              <Row label="Base Year CAM Pool" value={fmtCurrency(rec.base_year_cam_pool)} mono />
              <Row label="Base Year Taxes" value={fmtCurrency(rec.base_year_taxes)} mono />
              <Row label="Base Year Insurance" value={fmtCurrency(rec.base_year_insurance)} mono />
            </Section>
          )}
        </div>

        {/* Right column */}
        <div>
          {isCap && (
            <Section title="Expense Cap">
              <Row label="Cap %" value={fmtPercent(rec.cap_pct, 1)} mono />
              <Row label="Compounding" value={rec.compounding ? "Yes" : "No"} />
              <Row label="Capped Amount" value={fmtCurrency(rec.capped_amount)} mono />
              <Row label="Cap Formula" value={rec.cap_formula || "--"} />
            </Section>
          )}

          <Section title="CAM Reimbursement">
            <Row label="Formula" value={rec.cam_formula} />
            <Row label="Amount" value={fmtCurrency(rec.cam_reimbursement)} mono />
          </Section>

          <Section title="Tax Reimbursement">
            <Row label="Current Taxes" value={fmtCurrency(rec.current_taxes)} mono />
            <Row label="Formula" value={rec.tax_formula} />
            <Row label="Amount" value={fmtCurrency(rec.tax_reimbursement)} mono />
          </Section>

          <Section title="Insurance Reimbursement">
            <Row label="Current Insurance" value={fmtCurrency(rec.current_insurance)} mono />
            <Row label="Formula" value={rec.insurance_formula} />
            <Row label="Amount" value={fmtCurrency(rec.insurance_reimbursement)} mono />
          </Section>

          {rec.admin_fee_pct > 0 && (
            <Section title="Admin Fee">
              <Row label="Admin Fee %" value={fmtPercent(rec.admin_fee_pct, 1)} mono />
              <Row label="Admin Fee Amount" value={fmtCurrency(rec.admin_fee_amount)} mono />
            </Section>
          )}

          <Section title="Total">
            <Row label="Formula" value={rec.total_formula} />
            <div className="flex justify-between py-1.5 border-t border-grove-300 mt-1">
              <span className="text-xs font-semibold text-bark-700">Total Reimbursement</span>
              <span className="text-xs font-bold text-forest-700 tabular-nums">
                {fmtCurrency(rec.total_reimbursement)}
              </span>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

// ---------- YoY comparison table ----------

function YoyTable({ records }: { records: ReimbursementPoolAudit[] }) {
  const sorted = useMemo(
    () => [...records].sort((a, b) => a.year - b.year),
    [records],
  );

  if (sorted.length === 0) return null;

  const thCls = "text-[11px] font-semibold text-bark-400 uppercase tracking-wider";

  return (
    <div className="bg-white border border-grove-300 rounded-xl overflow-hidden shadow-sm">
      <div className="px-4 py-3 bg-grove-50 border-b border-grove-200">
        <h4 className="text-sm font-semibold text-bark-700">Year-over-Year Reimbursement</h4>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-grove-200 bg-grove-50/50">
              <th className={`text-left px-4 py-2.5 ${thCls}`}>Year</th>
              <th className={`text-right px-4 py-2.5 ${thCls}`}>CAM</th>
              <th className={`text-right px-4 py-2.5 ${thCls}`}>Tax</th>
              <th className={`text-right px-4 py-2.5 ${thCls}`}>Insurance</th>
              <th className={`text-right px-4 py-2.5 ${thCls}`}>Total</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.year} className="border-b border-grove-200 last:border-0 hover:bg-grove-50 transition-colors">
                <td className="px-4 py-2.5 text-xs text-bark-700">
                  Yr {r.year} ({r.calendar_year})
                </td>
                <td className="px-4 py-2.5 text-xs text-bark-600 tabular-nums text-right whitespace-nowrap">
                  {fmtCurrency(r.cam_reimbursement)}
                </td>
                <td className="px-4 py-2.5 text-xs text-bark-600 tabular-nums text-right whitespace-nowrap">
                  {fmtCurrency(r.tax_reimbursement)}
                </td>
                <td className="px-4 py-2.5 text-xs text-bark-600 tabular-nums text-right whitespace-nowrap">
                  {fmtCurrency(r.insurance_reimbursement)}
                </td>
                <td className="px-4 py-2.5 text-xs font-semibold text-bark-800 tabular-nums text-right whitespace-nowrap">
                  {fmtCurrency(r.total_reimbursement)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Main component ----------

export default function ReimbursementTab({
  dealId,
  scenarioId,
  tenants,
  holdYears,
}: ReimbursementTabProps) {
  const [selectedTenantId, setSelectedTenantId] = useState<string>(tenants[0]?.tenant_id || "");
  const years = useMemo(() => Array.from({ length: holdYears }, (_, i) => i + 1), [holdYears]);
  const [selectedYear, setSelectedYear] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [record, setRecord] = useState<ReimbursementPoolAudit | null>(null);
  const [allYears, setAllYears] = useState<ReimbursementPoolAudit[]>([]);

  const loadSingle = useCallback(async (tenantId: string, year: number) => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchReimbursementAudit(dealId, scenarioId, tenantId, year);
      setRecord(result.length > 0 ? result[0] : null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load reimbursement audit");
    } finally {
      setLoading(false);
    }
  }, [dealId, scenarioId]);

  const loadAllYears = useCallback(async (tenantId: string) => {
    if (!tenantId) return;
    try {
      const result = await fetchReimbursementAudit(dealId, scenarioId, tenantId);
      setAllYears(result);
    } catch {
      // non-critical — YoY table just won't show
    }
  }, [dealId, scenarioId]);

  useEffect(() => {
    if (selectedTenantId) {
      loadSingle(selectedTenantId, selectedYear);
      loadAllYears(selectedTenantId);
    }
  }, [selectedTenantId, selectedYear, loadSingle, loadAllYears]);

  if (tenants.length === 0) {
    return (
      <div className="bg-white border border-grove-300 rounded-xl p-8 text-center">
        <p className="text-sm text-bark-400">No tenants found for this deal.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Selectors */}
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="reimb-tenant" className="text-sm font-medium text-bark-600">Tenant</label>
        <select
          id="reimb-tenant"
          value={selectedTenantId}
          onChange={(e) => setSelectedTenantId(e.target.value)}
          className="flex-1 max-w-md px-3 py-2 text-sm border border-grove-300 rounded-lg bg-white text-bark-700 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
        >
          {tenants.map((t) => (
            <option key={t.tenant_id} value={t.tenant_id}>
              {t.tenant_name}{t.suite_id ? ` (${t.suite_id})` : ""} {"\u2014"} {fmtSF(t.leased_sf)}
            </option>
          ))}
        </select>

        <label htmlFor="reimb-year" className="text-sm font-medium text-bark-600 ml-2">Year</label>
        <select
          id="reimb-year"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="px-3 py-2 text-sm border border-grove-300 rounded-lg bg-white text-bark-700 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
        >
          {years.map((y) => (
            <option key={y} value={y}>Year {y}</option>
          ))}
        </select>
        <button
          onClick={() => {
            if (allYears.length === 0) return;
            const sorted = [...allYears].sort((a, b) => a.year - b.year);
            const headers: (string | number)[] = ["Year", "CAM Type", "Pro-Rata Share", "CAM Reimbursement", "Tax Reimbursement", "Insurance Reimbursement", "Total Reimbursement"];
            const rows = sorted.map((r) => [
              `Yr ${r.year} (${r.calendar_year})`,
              fmtCamType(r.cam_type),
              fmtPercent(r.computed_pro_rata, 1),
              Math.round(r.cam_reimbursement),
              Math.round(r.tax_reimbursement),
              Math.round(r.insurance_reimbursement),
              Math.round(r.total_reimbursement),
            ]);
            const tenantName = tenants.find((t) => t.tenant_id === selectedTenantId)?.tenant_name || "tenant";
            downloadCSV([headers, ...rows], `audit_reimbursement_${tenantName}.csv`);
          }}
          disabled={allYears.length === 0}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all disabled:opacity-50 print:hidden ml-auto"
        >
          <Download className="w-4 h-4" />
          CSV
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : !record ? (
        <div className="bg-white border border-grove-300 rounded-xl p-8 text-center">
          <p className="text-sm text-bark-400">
            No reimbursement data for this tenant / year combination.
          </p>
        </div>
      ) : (
        <>
          <BreakdownCard rec={record} />
          <YoyTable records={allYears} />
        </>
      )}
    </div>
  );
}
